/**
 * dateUtils.js
 * Safe cross-browser date parsing utility.
 *
 * Problem: Safari / iOS WebKit does NOT support:
 *   new Date('2024-01-15')          → "Invalid Date" in Safari
 *   new Date('2024-01-15 10:30:00') → "Invalid Date" in Safari
 *
 * Solution: Use this utility everywhere instead of raw new Date(string).
 *
 * Usage:
 *   import { safeDate, formatDate, formatDateTime, timeAgo } from './dateUtils';
 */

/**
 * Safely parse any date string - works on iOS Safari, Chrome, Firefox, Edge.
 * @param {string|Date|null|undefined} input
 * @returns {Date|null}
 */
export function safeDate(input) {
    if (!input) return null;
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;

    const str = String(input).trim();
    if (!str) return null;

    // Already a timestamp number
    if (/^\d+$/.test(str)) return new Date(Number(str));

    // ISO 8601: "2024-01-15" → Safari rejects this, convert to "2024/01/15"
    // "2024-01-15T10:30:00Z" → keep as-is (Safari supports T format)
    // "2024-01-15 10:30:00" → Safari rejects the space, convert to "T"
    const safStr = str
        .replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$1/$2/$3')           // "2024-01-15" → "2024/01/15"
        .replace(/^(\d{4})-(\d{2})-(\d{2}) /, '$1/$2/$3T');          // "2024-01-15 10:30" → "2024/01/15T10:30"

    const d = new Date(safStr);
    return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date as "15 Jan 2024" (en-IN style)
 * @param {string|Date|null} input
 * @param {string} fallback - what to show if date is invalid
 * @returns {string}
 */
export function formatDate(input, fallback = '—') {
    const d = safeDate(input);
    if (!d) return fallback;
    try {
        return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
    } catch {
        return fallback;
    }
}

/**
 * Format a date as "15 Jan 2024, 10:30 AM"
 * @param {string|Date|null} input
 * @param {string} fallback
 * @returns {string}
 */
export function formatDateTime(input, fallback = '—') {
    const d = safeDate(input);
    if (!d) return fallback;
    try {
        return d.toLocaleString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata'
        });
    } catch {
        return fallback;
    }
}

/**
 * Format a date as "Jan 2024"
 * @param {string|Date|null} input
 * @param {string} fallback
 * @returns {string}
 */
export function formatMonthYear(input, fallback = '—') {
    const d = safeDate(input);
    if (!d) return fallback;
    try {
        return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
    } catch {
        return fallback;
    }
}

/**
 * Time ago string — "2 hours ago", "3 days ago"
 * @param {string|Date|null} input
 * @returns {string}
 */
export function timeAgo(input) {
    const d = safeDate(input);
    if (!d) return '';
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60)     return 'just now';
    if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return formatDate(d);
}

/**
 * Check if a date string is valid cross-browser
 * @param {string|Date|null} input
 * @returns {boolean}
 */
export function isValidDate(input) {
    return safeDate(input) !== null;
}
